<template>
  <div class="sidebar vh-100" v-if="org" :class="sidebarStateClass">
    <div
      class="sidebar-opened col-auto h-100"
      :style="{ width: toPixel(sidebarWidth) }"
      @mouseleave="minimizeIfFloating"
    >
      <div class="logo-wrapper px-3 mx-1 d-flex justify-content-between align-items-center">
        <router-link class="d-flex bg-doc" :to="yourWorkPageLocation"
          ><i class="logo-round-icon d-inline-block"></i
        ></router-link>
        <div class="close-icon-wrapper flex-center" v-if="showClose" @click="$emit('close')">
          <i class="close-icon"></i>
        </div>
        <template v-if="showHideIcon">
          <b-button
            class="p-0 text-secondary text-medium pin-button"
            v-if="sidebarState === 'EXPANDED'"
            variant="link"
            @click="collapseSidebar"
            id="hide-nav"
            >hide
          </b-button>
          <b-button
            class="p-0 text-secondary text-medium pin-button"
            v-else-if="sidebarState === 'FLOATING'"
            variant="link"
            @click="expandSidebar"
            >pin
          </b-button>
        </template>
        <action-tooltip
          v-if="sidebarState === 'EXPANDED'"
          target="hide-nav"
          text="Hide nav"
          :actions="['[']"
        />
      </div>
      <div class="sidebar-items flex-grow-1 px-2 text-medium d-flex-col scroll-secondary mt-2">
        <ul class="nav flex-column flex-nowrap" @click="$emit('close')">
          <inbox-link :org-id="orgId" id="inbox-nav" />
          <li class="nav-item nav-toplevel" id="your-work-nav">
            <router-link class="nav-top-link radial-hover" :to="yourWorkPageLocation"
              ><i class="your-work-icon"></i><span>Your work</span>
            </router-link>
          </li>
          <action-tooltip
            placement="right"
            target="your-work-nav"
            text="Your Work"
            :actions="['w']"
          />
          <li class="nav-item nav-toplevel mb-3" id="org-goals-nav">
            <router-link
              class="nav-top-link radial-hover text-truncate"
              :to="orgGoalsPageLocation"
              :class="{ 'router-link-active': isActiveTeam(subOrg.id) }"
              :id="'sidebar-team-' + subOrgId"
              ><i
                class="flex-shrink-0"
                :class="isRootTeamId(subOrgId) ? 'org-icon' : 'child-org-icon'"
              ></i
              ><span class="text-truncate" :title="orgGoalsTitle" v-no-capture>{{
                orgGoalsTitle
              }}</span>
            </router-link>
            <ancestors-popover
              :key="subOrgId"
              v-if="subOrgId && !isRootTeamId(subOrgId)"
              :target="'sidebar-team-' + subOrgId"
              :team-id="subOrgId"
            />
          </li>
          <starred-links :org-id="orgId" :sub-org-id="subOrgId" />
          <action-tooltip
            placement="right"
            target="org-goals-nav"
            text="Org goals"
            :actions="['o']"
          />
          <li class="nav-item">
            <div
              class="d-flex align-items-center position-relative pointer caret-link"
              @click="toggleTeamsVisibility"
              id="team-goals-nav"
            >
              <collapse-caret
                class="flex-shrink-0 collapse-section"
                :size="16"
                :expanded="teamsShown"
                :container-to-icon-ratio="0.5"
              />
              <div
                class="nav-toplevel nav-toplevel-item d-flex justify-content-between align-items-center flex-grow-1 nav-item py-1 plus-icon-container"
              >
                <div class="nav-top-link"><i class="team-goals-icon"></i> <span>Teams</span></div>
                <action-icon
                  icon="plus-icon"
                  class="plus-icon-component"
                  @click.stop="showAddTeam = true"
                  tooltip-text="Add a new Team"
                />
              </div>
              <action-tooltip
                placement="right"
                v-if="sidebarState === 'EXPANDED'"
                :target="'create-team-' + uuid"
                text="Add a new Team"
              />
            </div>
            <b-collapse v-model="teamsShown">
              <ul class="nav flex-column">
                <template v-for="team in teams">
                  <sidebar-team-accordion :team="team" :key="team.id" v-if="isProjectsEnabled" />
                  <team-link
                    class="left-spacing"
                    :team="team"
                    :show-icon="isProjectsEnabled"
                    :key="team.id"
                    :label="isProjectsEnabled ? team.name + ' Goals' : team.name"
                    v-else
                  />
                </template>
                <li class="mb-1 left-spacing" v-if="teams.length === 0">
                  <b-button
                    variant="link"
                    class="text-secondary text-medium"
                    @click.stop="showAddTeam = true"
                    >Add team
                  </b-button>
                </li>
              </ul>
            </b-collapse>
          </li>
        </ul>
        <user-lists-links :org-id="orgId" :sub-org-id="subOrgId" @close="$emit('close')" />
        <ul class="list-unstyled">
          <li class="nav-item nav-toplevel">
            <explore-all-link :org-id="orgId" :sub-org-id="subOrgId" />
          </li>
        </ul>
      </div>
      <ul class="nav bottom-nav flex-nowrap flex-column px-2">
        <connect-to-chat-widget :org-id="orgId" />
        <free-plan-link :org-id="orgId" :sub-org-id="subOrgId" />
        <!-- Note: id - "setting-nav" is getting used at multiple places. e.g to tour --->
        <li
          class="nav-item d-flex align-items-center justify-content-between pt-2"
          id="settings-nav"
        >
          <!-- Note: id - "profile-nav" is getting used to show tooltip --->
          <div
            class="d-flex user-info overflow-hidden"
            @click="showUserInfoMenu = !showUserInfoMenu"
            ref="userInfo"
            id="profile-nav"
          >
            <user-short :user-id="user.id" length="24" class="pr-2"></user-short>
            <div class="mw-0 overflow-hidden">
              <div class="text-truncate">
                <strong :title="user.displayName" v-no-capture>{{ user.displayName }}</strong>
              </div>
              <div class="text-truncate d-flex align-items-baseline">
                <div class="text-truncate pr-1" :title="subOrg.name" v-no-capture>
                  {{ subOrg.name }}
                </div>
                <i class="caret-heavy-down-icon flex-shrink-0"></i>
              </div>
            </div>
          </div>
          <div class="ml-2 flex-center pointer position-relative">
            <i
              class="question-circle-icon"
              @click="helpLinksShown = !helpLinksShown"
              v-click-outside="hideHelpMenu"
            ></i>
            <help-links-menu :org-id="orgId" v-if="helpLinksShown" />
          </div>
          <div
            v-if="showUserInfoMenu"
            class="user-info-menu menu-shadow scroll-secondary"
            v-click-outside="hideUserInfoMenu"
          >
            <div class="user-info-menu-group d-flex flex-column">
              <div class="user-info-menu-user d-flex pb-3">
                <user-short :user-id="user.id" length="32" class="pr-2"></user-short>
                <div class="text-truncate" v-no-capture>
                  <div class="text-truncate" :title="user.displayName">
                    <strong>{{ user.displayName }}</strong>
                  </div>
                  <div class="user-info-menu-email text-truncate" :title="user.email">
                    {{ user.email }}
                  </div>
                </div>
              </div>
              <div class="user-info-menu-orgs pl-2 pb-2 scroll-secondary scroll-shadow">
                <teams-list :org-id="orgId" @select="showUserInfoMenu = false" />
              </div>
            </div>
            <hr class="separator separator-small mr-0 mt-0" />
            <div class="user-info-menu-item radial-hover">
              <b-button
                variant="link"
                class="text-primary text-medium text-decoration-none"
                @click=";(showUserInfoMenu = false), (showInviteModal = true)"
                >+ Invite People
              </b-button>
            </div>
            <hr class="separator separator-small mr-0" />
            <div class="user-info-menu-item radial-hover">
              <b-button
                variant="link"
                class="text-primary text-medium text-decoration-none"
                @click=";(showUserInfoMenu = false), (showDemoOrgModal = true)"
                >Try a demo account
              </b-button>
            </div>
            <div class="user-info-menu-item radial-hover">
              <b-button
                variant="link"
                class="text-primary text-medium text-decoration-none"
                @click=";(showUserInfoMenu = false), (showUpdateProfile = true)"
                >Your profile
              </b-button>
            </div>
            <div class="user-info-menu-item radial-hover">
              <b-button
                variant="link"
                class="text-primary text-medium text-decoration-none"
                @click="onNotificationsLinkClick"
                >Notifications
              </b-button>
            </div>
            <div class="user-info-menu-item radial-hover" v-if="isUserOrgAdmin">
              <b-button
                variant="link"
                class="text-primary text-medium text-decoration-none"
                @click="onAdminLinkClick"
                >Admin panel
              </b-button>
            </div>
            <div class="user-info-menu-item radial-hover">
              <b-button
                variant="link"
                class="text-primary text-medium text-decoration-none"
                @click="onArchiveLinkClick"
                >Archive
              </b-button>
            </div>
            <div class="user-info-menu-item radial-hover">
              <b-button
                variant="link"
                class="text-primary text-medium text-decoration-none"
                @click="onAPILinkClick"
                >API
              </b-button>
            </div>
            <hr class="separator separator-small mr-0" />
            <div class="user-info-menu-item radial-hover">
              <router-link :to="{ name: 'logout' }">Sign out</router-link>
            </div>
          </div>
        </li>
      </ul>
      <resize-slider
        class="resize-slider"
        v-if="sidebarState === 'EXPANDED'"
        @resize="onSidebarResize"
      />
      <manage-team
        :group-type="GROUP_TYPE.TEAM"
        v-model="showAddTeam"
        :org-id="orgId"
        :sub-org-id="subOrgId"
        @add="onTeamCreation"
        v-if="showAddTeam"
      />
      <org-invite-modal v-model="showInviteModal" v-if="showInviteModal" :org-id="orgId" />
    </div>
    <div
      class="sidebar-minimized"
      v-if="sidebarState === 'MINIMIZED'"
      @mouseenter="onMinimizedSidebarMouseEnter"
      @mouseleave="onMinimizedSidebarMouseLeave"
    >
      <router-link class="bg-transparent d-inline-block" :to="yourWorkPageLocation"
        ><i class="logo-round-icon"></i
      ></router-link>
    </div>
    <update-profile v-if="showUpdateProfile" @hide="showUpdateProfile = false" />
    <demo-org-modal v-model="showDemoOrgModal" v-if="showDemoOrgModal" />
  </div>
</template>

<script>
import { trackEvent } from '@/analytics'
import ActionTooltip from '@/components/ActionTooltip'
import CollapseCaret from '@/components/CollapseCaret'
import DemoOrgModal from '@/components/demoOrg/DemoOrgModal.vue'
import AncestorsPopover from '@/components/goal/AncestorsPopover'
import ManageTeam from '@/components/ManageTeam'
import OrgInviteModal from '@/components/OrgInviteModal'
import ConnectToChatWidget from '@/components/sidebar/ConnectToChatWidget'
import ExploreAllLink from '@/components/sidebar/ExploreAllLink'
import FreePlanLink from '@/components/sidebar/FreePlanLink'
import HelpLinksMenu from '@/components/sidebar/HelpLinksMenu'
import InboxLink from '@/components/sidebar/InboxLink'
import ActionIcon from '@/components/ActionIcon.vue'
import ResizeSlider from '@/components/sidebar/ResizeSlider.vue'
import StarredLinks from '@/components/sidebar/StarredLinks.vue'
import SidebarTeamAccordion from '@/components/sidebar/SidebarTeamAccordion.vue'
import TeamLink from '@/components/sidebar/TeamLink.vue'
import UserListsLinks from '@/components/sidebar/UserListsLinks.vue'
import TeamsList from '@/components/team/TeamsList'
import UpdateProfile from '@/components/UpdateProfile'
import UserShort from '@/components/UserShort'
import { EVENT_ACTION, EVENT_CATEGORY } from '@/constants/analytics'
import { CONTEXT_TYPE } from '@/constants/common'
import { GROUP_TYPE } from '@/constants/entities'
import { featureFlags } from '@/constants/featureFlags'
import { goalsTabIds } from '@/constants/goals'
import { ctrlKeyLabel } from '@/constants/keyCodes'
import { ROUTE_NAMES } from '@/constants/routes'
import { STORAGE_KEYS } from '@/constants/storage'
import { TOAST_TYPE } from '@/constants/toasts'
import ComponentIdMixin from '@/mixins/ComponentIdMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import MediaQueryMixin from '@/mixins/MediaQueryMixin'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import SidebarMixin from '@/mixins/SidebarMixin'
import SidebarStorageMixins from '@/mixins/SidebarStorageMixins'
import TeamMixin from '@/mixins/TeamMixin'
import localStorage from '@/utils/localStorage'
import * as keymapService from '@/services/keymapService/keymapService'
import convertStringToBoolean from '@/utils/convertStringToBoolean'
import handleClickNavigation from '@/utils/handleClickNavigation'
import {
  getAdminPageRouteConfig,
  getArchivePageRouteConfig,
  getNotificationsPageRouteConfig,
  getProjectPageRouteConfig,
  getTeamPageRouteConfig,
  getYourWorkPageRouteConfig
} from '@/utils/navigationConfigs'
import showToast from '@/utils/showToast'
import toPixel from '@/utils/toPixel'
import { BButton, BCollapse } from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'

/**
 * This is the Sidebar component that can be used to navigate to different section of the
 * app like - User Goal, All Goals etc
 */
export default {
  name: 'Sidebar',
  components: {
    ResizeSlider,
    TeamLink,
    SidebarTeamAccordion,
    DemoOrgModal,
    BButton,
    UserShort,
    ManageTeam,
    OrgInviteModal,
    UpdateProfile,
    ActionTooltip,
    BCollapse,
    TeamsList,
    AncestorsPopover,
    CollapseCaret,
    UserListsLinks,
    InboxLink,
    ExploreAllLink,
    HelpLinksMenu,
    StarredLinks,
    ConnectToChatWidget,
    FreePlanLink,
    ActionIcon
  },
  props: ['orgId', 'showClose', 'showHideIcon'],
  data() {
    const sidebarWidth = localStorage.getItem(STORAGE_KEYS.sidebarWidth) || 240
    return {
      showUserInfoMenu: false,
      showAddTeam: false,
      showInviteModal: false,
      showUpdateProfile: false,
      sidebarState: 'EXPANDED',
      ctrlKeyLabel,
      ROUTE_NAMES,
      teamsShown: this.getSidebarFlag(STORAGE_KEYS.sidebar.flags.teamsExpanded),
      goalsTabIds,
      GROUP_TYPE,
      helpLinksShown: false,
      showDemoOrgModal: false,
      sidebarWidth
    }
  },
  mixins: [
    MembershipsMixin,
    TeamMixin,
    ComponentIdMixin,
    SidebarMixin,
    SidebarStorageMixins,
    MediaQueryMixin,
    FeaturesMixin
  ],
  computed: {
    ...mapGetters('starred', ['starred']),
    isProjectsEnabled() {
      return !!this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_PROJECTS)
    },
    subOrgId() {
      return this.$store.state.orgSelect.selectedOrgLikeTeamId
    },
    isUserOrgAdmin() {
      return this.subOrg.memberships?.some(
        user => user.type === 'owner' && user.userId === this.user.id
      )
    },
    teams() {
      return this.$store.state.user.teams
        .filter(team => team.parentId === this.subOrgId && !team.isOrgLike && !team.isArchived)
        .sort((a, b) => {
          return a.name.localeCompare(b.name)
        })
    },
    orgs() {
      return this.$store.getters.userOrgs
    },
    subOrg() {
      return this.$store.getters.teamById(this.subOrgId) || {}
    },
    orgGoalsTitle() {
      return `${this.subOrg.name} (org goals)`
    },
    user() {
      return this.$store.getters.user
    },
    org() {
      return this.$store.getters.orgById(this.orgId)
    },
    sidebarStateClass() {
      return {
        floating: this.sidebarState === 'FLOATING',
        minimized: this.sidebarState === 'MINIMIZED',
        opened: this.sidebarState === 'EXPANDED'
      }
    },
    yourWorkPageLocation() {
      return getYourWorkPageRouteConfig({
        orgId: this.orgId,
        subOrgId: this.subOrgId
      })
    },
    orgGoalsPageLocation() {
      return getTeamPageRouteConfig({
        orgId: this.orgId,
        teamId: this.subOrgId
      })
    },
    starredTeamIds() {
      return this.starred?.filter(x => x.contextType === CONTEXT_TYPE.TEAM).map(x => x.contextId)
    }
  },
  methods: {
    ...mapActions('starred', ['getStarred', 'cleanStarred']),
    ...mapActions('project', ['getTeamProjects']),
    toPixel,
    hideUserInfoMenu(event) {
      let userInfoEl = this.$el.querySelector('.user-info')
      if (!(event.target === userInfoEl || userInfoEl.contains(event.target))) {
        this.showUserInfoMenu = false
      }
    },
    hideHelpMenu() {
      this.helpLinksShown = false
    },
    minimizeIfFloating() {
      if (this.sidebarState === 'FLOATING' && !this.showUserInfoMenu) {
        this.sidebarState = 'MINIMIZED'
      }
    },
    onMinimizedSidebarMouseEnter() {
      this.peekTimeoutId = setTimeout(() => {
        if (this.sidebarState === 'MINIMIZED') {
          this.sidebarState = 'FLOATING'
          trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.PEEK_SIDEBAR)
          this.peekTimeoutId = null
        }
      }, 500)
    },
    onMinimizedSidebarMouseLeave() {
      if (this.peekTimeoutId) {
        clearTimeout(this.peekTimeoutId)
        this.peekTimeoutId = null
      }
    },
    collapseSidebar() {
      this.sidebarState = 'MINIMIZED'
      trackEvent(EVENT_CATEGORY.PREFERENCE, EVENT_ACTION.HIDE_SIDEBAR)
    },
    expandSidebar() {
      this.sidebarState = 'EXPANDED'
      trackEvent(EVENT_CATEGORY.PREFERENCE, EVENT_ACTION.PIN_SIDEBAR)
    },
    goToAdminPage(event) {
      const location = getAdminPageRouteConfig({
        orgId: this.orgId,
        subOrgId: this.subOrgId
      })
      handleClickNavigation.call(this, event, location)
    },
    onAdminLinkClick(event) {
      this.showUserInfoMenu = false
      this.goToAdminPage(event)
    },
    onNotificationsLinkClick(event) {
      this.showUserInfoMenu = false
      const location = getNotificationsPageRouteConfig({
        orgId: this.orgId
      })
      handleClickNavigation.call(this, event, location)
    },
    onArchiveLinkClick(event) {
      this.showUserInfoMenu = false
      const archivePageLocation = getArchivePageRouteConfig({
        orgId: this.orgId,
        subOrgId: this.subOrgId
      })
      handleClickNavigation.call(this, event, archivePageLocation)
    },
    onAPILinkClick(event) {
      this.showUserInfoMenu = false
      const location = { name: ROUTE_NAMES.API, params: { orgId: this.orgId } }
      handleClickNavigation.call(this, event, location)
    },
    navigateToProjectPage(project) {
      const projectPageLocation = getProjectPageRouteConfig({
        orgId: project.organizationId,
        projectId: project.id
      })
      this.$router.push(projectPageLocation)
    },
    async onTeamCreation(team) {
      if (team.preferences.enableGoals) {
        this.navigateToTeamPage(team)
      } else if (team.preferences.enableProjects) {
        const projects = this.$store.getters['project/projectsByTeamId'](team.id)
        if (projects?.length > 0) {
          this.navigateToProjectPage(projects[0])
        }
      }
    },
    navigateToTeamPage(team) {
      const location = getTeamPageRouteConfig({
        orgId: this.orgId,
        entity: goalsTabIds.goals,
        teamId: team.id
      })
      this.$router.push(location)
    },
    getTeamPageLocation(teamId) {
      return getTeamPageRouteConfig({ orgId: this.orgId, teamId })
    },
    refreshStarredTeams() {
      this.getStarred(this.orgId).catch(() =>
        showToast('Unable to fetch starred items', TOAST_TYPE.ERROR)
      )
    },
    toggleTeamsVisibility() {
      this.teamsShown = !this.teamsShown
      this.setSidebarFlag(STORAGE_KEYS.sidebar.flags.teamsExpanded, this.teamsShown)
    },
    toggleSidebar() {
      if (this.sidebarState === 'EXPANDED') {
        this.collapseSidebar()
      } else {
        this.expandSidebar()
      }
    },
    checkDemoOrgModalFlag() {
      if (convertStringToBoolean(this.$route.query.showDemoModal)) {
        this.showDemoOrgModal = true
      }
    },
    onSidebarResize(value) {
      this.sidebarWidth = value
      localStorage.setItem(STORAGE_KEYS.sidebarWidth, value)
    }
  },
  mounted() {
    this.isDesktopMode && this.$store.dispatch('announcement/getNewAnnouncements')
    keymapService.activateCategory('sidebar', {
      'toggle-sidebar': this.toggleSidebar
    })
    this.refreshStarredTeams()
    this.checkDemoOrgModalFlag()
  },
  beforeDestroy() {
    keymapService.deactivateCategory('sidebar')
  },
  watch: {
    showUserInfoMenu() {
      this.minimizeIfFloating()
      const team = this.$store.getters.teamById(this.subOrgId)
      if (!team.memberships) {
        this.fetchTeamMemberships(this.orgId, this.subOrgId)
      }
    },
    subOrgId() {
      this.cleanStarred()
      this.refreshStarredTeams()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';
@import '~@/styles/_hoverStates.scss';
@import '~@/styles/animations/_ring.scss';
@import './styles/rearrangableSidebarLinks';

$iconSize: 16px;
$linkBorderRadius: 8px;

.sidebar {
  z-index: 4; /* Need this to make sidebar come on top of header bg image and initiative table header*/
}

.sidebar-opened {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 0 8px 0;
  background-color: $documentColor;
}

.user-info {
  font-size: 14px;
  color: $text-muted;
  cursor: pointer;
}

.user-info-menu {
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 60px;
  left: 36px;
  padding: 20px 8px;
  font-size: 14px;
  z-index: 1;
  border-radius: 6px;
  min-width: 240px;
  max-width: 360px;
  background-color: white;
  max-height: 80%;
}

.user-info-menu-email {
  font-size: 11px;
  color: $text-muted;
}

.user-info-menu a {
  color: $dark;
  text-decoration: none;
}

.user-info-menu .separator {
  margin-top: 8px;
  margin-bottom: 8px;
}

.user-info-menu .separator-small {
  margin-left: 28px;
}

.user-info-menu-simple-link {
  margin-left: 28px;
}

.new-updates {
  margin-left: 2px;
}

.close-icon-wrapper {
  width: 42px;
  height: 42px;

  .close-icon {
    width: 18px;
    height: 18px;
    background-position: center;
    background-size: contain;
    cursor: pointer;
  }
}

.logo {
  height: 30px;
  background-position: 0 center;
}

.logo-wrapper {
  padding-top: 8px;
  padding-bottom: 8px;
}

.user-info-menu-item {
  padding-left: 16px;
  flex-shrink: 0;
}

.sidebar.minimized {
  width: 60px;
}

.sidebar.minimized {
  position: fixed;
  background: transparent;
}

.sidebar.minimized .sidebar-opened,
.sidebar.floating .sidebar-opened {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 1;
  box-shadow: 2px 0 2px 0 rgba(65, 65, 65, 0.1);
  overflow: visible;
}

.sidebar-opened {
  will-change: transform, opacity;
  transition: transform 0.2s ease-in, opacity 0.15s ease-in;
}

.sidebar.minimized .sidebar-opened {
  transform: translate3d(-105%, 0, 0);
  overflow: hidden;
  opacity: 0;
}

.sidebar.floating .sidebar-opened {
  transition: transform 0.2s cubic-bezier(0, 0.85, 0.4, 0.91),
    opacity 0.5s cubic-bezier(0, 0.85, 0.4, 0.91);
  transform: translate3d(0, 0, 0);
  opacity: 1;
}

.logo-round-icon {
  width: 20px;
  height: 20px;
  view-transition-name: round-logo;
}

.left-spacing {
  padding-left: $leftSpacing;
}

.sidebar-minimized .logo-round-icon {
  width: 25px;
  height: 25px;
}

.sidebar-minimized {
  padding-top: 10px;
  padding-left: 10px;
  padding-right: 10px;
}

.pin-button {
  opacity: 0;
  transition: opacity 100ms ease-in;
}

.sidebar:hover .pin-button {
  opacity: 1;
}

.user-info-menu-item a {
  padding: 0.375rem 0.75rem;
}

.nav-sublever:first-child {
  padding-top: 9px;
}

/deep/ {
  .collapse-section {
    position: absolute !important;
    left: -6px;
  }

  .nav-top-link {
    display: flex;
    align-items: center;

    i {
      margin-right: 4px;
    }
  }

  .nav-toplevel {
    font-weight: 500;
  }

  .nav-toplevel > a {
    padding: 4px 12px;
  }

  .nav-sublevel > a {
    padding: 2px 12px;
  }

  .nav-item {
    padding-top: 2px;
    padding-bottom: 2px;
    width: 100%;
  }

  .nav-toplevel-item {
    padding-left: ($spacer * 0.75);
    padding-right: ($spacer * 0.75);
  }

  .nav-sublevel:last-child {
    padding-bottom: 9px;
  }

  .nav-sublevel {
    padding-top: 2px;
    padding-bottom: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sidebar-opened a {
    color: $primary;
    text-decoration: none;
    border-radius: $linkBorderRadius;
    display: flex;
  }

  .sidebar-opened .router-link-active:not(.bg-doc):not(.bg-transparent) {
    background: $quinary;
    outline: none;
  }
}

.reports-icon {
  @include dimension($iconSize, $iconSize);
  background-size: auto;
}

.child-org-icon,
.org-icon,
.learn-north-icon {
  @include dimension($iconSize, $iconSize);
}

.user-info-menu-user {
  padding-left: 7px;
}

.collapse-section,
.plus-icon-component {
  opacity: 0;
}

.caret-link:hover .collapse-section,
.plus-icon-container:hover .plus-icon-component {
  opacity: 1;
}

.resize-slider {
  opacity: 0;
  transition: opacity linear 100ms;

  &.active {
    opacity: 1;
  }
}

.sidebar-opened:hover .resize-slider {
  opacity: 1;
}

@include media-breakpoint-down(md) {
  .user-info-menu {
    bottom: 85px;
    left: 20px;
    right: 20px;
    width: auto;
  }
  .sidebar.opened {
    width: 100%;
    padding: 20px 0;
    height: 100% !important;
  }
  .sidebar-items {
    font-size: 1.125rem;
    padding: 0 28px !important;
  }
  .logo-wrapper {
    padding-top: 0;
    margin: 0 24px !important;
  }
  .bottom-nav {
    padding: 0 28px !important;
  }
  .user-info-menu {
    box-shadow: 0px 10px 30px #9f9f9f;
  }
  .collapse-section {
    left: -12px;
    top: 9px;
  }
}
</style>

<style lang="scss">
.sidebar.minimized,
.sidebar.floating {
  + main .app-content-region {
    padding-left: 50px !important;
  }
}
</style>
